/**
 * Patient-Facing Explainability Component
 * Shows risk score explanations in patient-friendly language
 */
import React, { useState, useEffect } from 'react';
import api from '../api/client';
import { 
  Info, 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Download,
  Shield
} from 'lucide-react';

const RiskExplanation = ({ assessmentId }) => {
  const [explanation, setExplanation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (assessmentId) {
      fetchExplanation();
    }
  }, [assessmentId]);

  const fetchExplanation = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/transparency/risk-explanation/${assessmentId}`);
      if (response.data.success) {
        setExplanation(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching explanation:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving': return <TrendingDown className="w-5 h-5 text-success-500" />;
      case 'worsening': return <TrendingUp className="w-5 h-5 text-orange-500" />;
      case 'critical': return <AlertTriangle className="w-5 h-5 text-danger-600" />;
      default: return <Minus className="w-5 h-5 text-surface-400" />;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'improving': return 'text-success-600 bg-success-50';
      case 'worsening': return 'text-orange-600 bg-orange-50';
      case 'critical': return 'text-danger-600 bg-danger-50';
      default: return 'text-surface-600 bg-surface-50';
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-4 bg-surface-200 rounded w-3/4"></div>
        <div className="h-4 bg-surface-200 rounded w-1/2"></div>
      </div>
    );
  }

  if (!explanation) {
    return null;
  }

  return (
    <div className="bg-white rounded-2xl border border-outline-variant/10 overflow-hidden shadow-premium">
      {/* Header */}
      <div className="p-6 border-b border-surface-100">
        <div className="flex items-center gap-2 mb-2">
          <Info className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-surface-900">
            Understanding Your Risk Score
          </h3>
        </div>
        <p className="text-surface-600">
          {explanation.summary}
        </p>
      </div>

      {/* Trend */}
      {explanation.trendExplanation && (
        <div className="p-6 border-b border-surface-100">
          <div className="flex items-center gap-3">
            {getTrendIcon(explanation.trendExplanation.trend)}
            <div>
              <p className="font-medium text-surface-900">
                {explanation.trendExplanation.title}
              </p>
              <p className="text-sm text-surface-500">
                {explanation.trendExplanation.description}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Risk Breakdown */}
      <div className="p-6 border-b border-surface-100">
        <h4 className="font-semibold text-surface-900 mb-4">
          What Makes Up Your Score
        </h4>
        <div className="space-y-3">
          {(explanation.breakdown || []).map((component, idx) => (
            <div 
              key={idx}
              className="p-4 bg-surface-50 rounded-xl"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-surface-700">
                  {component.name}
                </span>
                <span className={`text-sm font-semibold px-2 py-1 rounded ${
                  component.score >= 60 ? 'bg-danger-100 text-danger-700' :
                  component.score >= 40 ? 'bg-orange-100 text-orange-700' :
                  'bg-success-100 text-success-700'
                }`}>
                  {component.score}/100
                </span>
              </div>
              <p className="text-sm text-surface-500">
                {component.explanation}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Contributing Factors */}
      <div className="p-6 border-b border-surface-100">
        <h4 className="font-semibold text-surface-900 mb-4">
          Factors Affecting Your Health
        </h4>
        <div className="space-y-2">
          {(explanation.contributingFactors || []).map((factor, idx) => (
            <div key={idx} className="flex items-start gap-3 p-3 bg-surface-50 rounded-lg">
              <div className={`w-2 h-2 rounded-full mt-2 ${
                factor.impact === 'High Impact' ? 'bg-danger-500' :
                factor.impact === 'Moderate Impact' ? 'bg-orange-500' :
                'bg-yellow-500'
              }`} />
              <div className="flex-1">
                <p className="font-medium text-surface-700">
                  {factor.factor}
                </p>
                <p className="text-sm text-surface-500">
                  {factor.explanation}
                </p>
                <span className="text-xs text-surface-400">
                  {factor.impact}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Improvement Suggestions */}
      <div className="p-6 border-b border-surface-100">
        <h4 className="font-semibold text-surface-900 mb-4">
          How to Improve Your Score
        </h4>
        <div className="space-y-3">
          {(explanation.improvementSuggestions || []).map((suggestion, idx) => (
            <div key={idx} className="p-4 bg-success-50 border border-success-200 rounded-xl">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-success-900">
                    {suggestion.area}: {suggestion.action}
                  </p>
                  <p className="text-sm text-success-700 mt-1">
                    {suggestion.impact}
                  </p>
                  <p className="text-xs text-success-600 mt-1">
                    {suggestion.how}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recommended Actions */}
      <div className="p-6 border-b border-surface-100">
        <h4 className="font-semibold text-surface-900 mb-4">
          Why These Actions Were Recommended
        </h4>
        <div className="space-y-3">
          {(explanation.actionExplanations || []).map((action, idx) => (
            <div key={idx} className="p-4 bg-primary-50 border border-primary-200 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-semibold px-2 py-1 rounded ${
                  action.priority === 'critical' ? 'bg-danger-100 text-danger-700' :
                  action.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                  'bg-primary-100 text-primary-700'
                }`}>
                  {action.priority}
                </span>
                <span className="font-medium text-surface-700">
                  {action.title}
                </span>
              </div>
              <p className="text-sm text-surface-600">
                {action.explanation}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Data Sources */}
      <div className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-primary-600" />
          <h4 className="font-semibold text-surface-900">
            Data Sources
          </h4>
        </div>
        <p className="text-sm text-surface-500 mb-3">
          This assessment was based on:
        </p>
        <div className="space-y-2">
          {(explanation.dataSources || []).map((source, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-surface-50 rounded-lg">
              <span className="text-sm text-surface-700">
                {source.type}
              </span>
              <span className="text-xs text-surface-400">
                {source.timestamp ? new Date(source.timestamp).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          ))}
        </div>
        <p className="text-xs text-surface-400 mt-4">
          Your data is used only to provide care recommendations and is never sold to third parties.
        </p>
      </div>
    </div>
  );
};

export default RiskExplanation;
